import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  FormHelperText,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CheckIcon } from "@chakra-ui/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const SetupPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Color mode values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const inviteToken = searchParams.get("token");
    if (inviteToken) {
      setToken(inviteToken);
      validateToken(inviteToken);
    } else {
      setValidating(false);
      toast({
        title: "Invalid Link",
        description: "No invitation token found in URL",
        status: "error",
        duration: 5000,
      });
    }
  }, [searchParams]);

  useEffect(() => {
    // Check password strength
    const password = formData.password;
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    });
  }, [formData.password]);

  const validateToken = async (inviteToken) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/validate-invite`,
        { token: inviteToken }
      );

      if (response.data.valid) {
        setTokenValid(true);
        setUserEmail(response.data.email);
      } else {
        setTokenValid(false);
        toast({
          title: "Invalid or Expired Link",
          description:
            response.data.message || "This invitation link is no longer valid",
          status: "error",
          duration: 5000,
        });
      }
    } catch (error) {
      setTokenValid(false);
      toast({
        title: "Validation Error",
        description:
          error.response?.data?.message || "Failed to validate invitation link",
        status: "error",
        duration: 5000,
      });
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isPasswordValid = () => {
    return (
      passwordStrength.hasMinLength &&
      passwordStrength.hasUpperCase &&
      passwordStrength.hasLowerCase &&
      passwordStrength.hasNumber &&
      passwordStrength.hasSpecialChar
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid()) {
      toast({
        title: "Invalid Password",
        description: "Password does not meet security requirements",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are identical",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/setup-password`,
        {
          token,
          password: formData.password,
        }
      );

      toast({
        title: "Account Activated!",
        description:
          "Your password has been set successfully. Redirecting to login...",
        status: "success",
        duration: 3000,
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Error setting up password:", error);
      toast({
        title: "Setup Failed",
        description:
          error.response?.data?.message || "Failed to set up password",
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center">
        <Container maxW="md">
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4}>
                <Heading size="md">Validating Invitation...</Heading>
                <Text color={mutedColor}>
                  Please wait while we verify your invitation link
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center">
        <Container maxW="md">
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4}>
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Invalid Invitation</AlertTitle>
                    <AlertDescription>
                      This invitation link is invalid or has expired. Please
                      contact your HR administrator for a new invitation.
                    </AlertDescription>
                  </Box>
                </Alert>
                <Button
                  colorScheme="blue"
                  onClick={() => navigate("/login")}
                  width="full"
                >
                  Go to Login
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center">
      <Container maxW="md">
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <VStack spacing={6} as="form" onSubmit={handleSubmit}>
              <VStack spacing={2} width="full">
                <Heading size="lg">Set Up Your Password</Heading>
                <Text color={mutedColor} textAlign="center">
                  Welcome! Create a secure password for your account
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  {userEmail}
                </Text>
              </VStack>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box fontSize="sm">
                  <AlertTitle mb={1}>Password Requirements:</AlertTitle>
                  <AlertDescription>
                    <VStack align="start" spacing={1} fontSize="xs">
                      <Text
                        color={
                          passwordStrength.hasMinLength
                            ? "green.500"
                            : mutedColor
                        }
                      >
                        {passwordStrength.hasMinLength ? "✓" : "○"} At least 8
                        characters
                      </Text>
                      <Text
                        color={
                          passwordStrength.hasUpperCase
                            ? "green.500"
                            : mutedColor
                        }
                      >
                        {passwordStrength.hasUpperCase ? "✓" : "○"} One
                        uppercase letter
                      </Text>
                      <Text
                        color={
                          passwordStrength.hasLowerCase
                            ? "green.500"
                            : mutedColor
                        }
                      >
                        {passwordStrength.hasLowerCase ? "✓" : "○"} One
                        lowercase letter
                      </Text>
                      <Text
                        color={
                          passwordStrength.hasNumber ? "green.500" : mutedColor
                        }
                      >
                        {passwordStrength.hasNumber ? "✓" : "○"} One number
                      </Text>
                      <Text
                        color={
                          passwordStrength.hasSpecialChar
                            ? "green.500"
                            : mutedColor
                        }
                      >
                        {passwordStrength.hasSpecialChar ? "✓" : "○"} One
                        special character (!@#$%^&*)
                      </Text>
                    </VStack>
                  </AlertDescription>
                </Box>
              </Alert>

              <FormControl isRequired>
                <FormLabel>New Password</FormLabel>
                <InputGroup>
                  <Input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      icon={
                        showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                      }
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
                {formData.confirmPassword &&
                  formData.password === formData.confirmPassword && (
                    <FormHelperText color="green.500">
                      <CheckIcon mr={1} /> Passwords match
                    </FormHelperText>
                  )}
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                isLoading={loading}
                loadingText="Setting up..."
                isDisabled={
                  !isPasswordValid() ||
                  formData.password !== formData.confirmPassword
                }
              >
                Set Password & Activate Account
              </Button>

              <Text fontSize="xs" color={mutedColor} textAlign="center">
                By setting up your password, you agree to our Terms of Service
                and Privacy Policy
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default SetupPassword;
